export type IconFormat = 'svg' | 'png' | 'jpg' | 'jpeg' | 'gif'

export interface IconDefinition {
    path: string
    format: IconFormat
    alt?: string
    colorType?: 'fill' | 'stroke' | 'original'
}

// Icon mapping dictionary
export const ICON_MAP: Record<string, IconDefinition> = {
    // App icons (various sizes available)
    'app-16': {
        path: '/assets/icons/16x16.png',
        format: 'png',
        alt: 'App icon 16x16',
    },
    'app-24': {
        path: '/assets/icons/24x24.png',
        format: 'png',
        alt: 'App icon 24x24',
    },
    'app-32': {
        path: '/assets/icons/32x32.png',
        format: 'png',
        alt: 'App icon 32x32',
    },
    'app-48': {
        path: '/assets/icons/48x48.png',
        format: 'png',
        alt: 'App icon 48x48',
    },
    'app-64': {
        path: '/assets/icons/64x64.png',
        format: 'png',
        alt: 'App icon 64x64',
    },
    'app-96': {
        path: '/assets/icons/96x96.png',
        format: 'png',
        alt: 'App icon 96x96',
    },
    'app-128': {
        path: '/assets/icons/128x128.png',
        format: 'png',
        alt: 'App icon 128x128',
    },
    'app-256': {
        path: '/assets/icons/256x256.png',
        format: 'png',
        alt: 'App icon 256x256',
    },
    'app-512': {
        path: '/assets/icons/512x512.png',
        format: 'png',
        alt: 'App icon 512x512',
    },
    'app-1024': {
        path: '/assets/icons/1024x1024.png',
        format: 'png',
        alt: 'App icon 1024x1024',
    },

    // Default app icon (medium size)
    app: {
        path: '/assets/icons/64x64.png',
        format: 'png',
        alt: 'App icon',
    },

    // Common UI icons (SVG files - loaded via HTTP from public folder)
    // Note: SVGs are now served from public/assets/icons/ directory
    search: {
        path: '/assets/icons/search.svg',
        format: 'svg',
        colorType: 'fill',
        alt: 'Search',
    },
    calendar: {
        path: '/assets/icons/calendar.svg',
        format: 'svg',
        alt: 'Calendar',
    },
    check: {
        path: '/assets/icons/check.svg',
        format: 'svg',
        alt: 'Check',
    },
    download: {
        path: '/assets/icons/download.svg',
        format: 'svg',
        alt: 'Download',
    },
    close: {
        path: '/assets/icons/close.svg',
        format: 'svg',
        alt: 'Close',
    },
    menu: {
        path: '/assets/icons/menu.svg',
        format: 'svg',
        alt: 'Menu',
    },
    settings: {
        path: '/assets/icons/menu.svg',
        format: 'svg',
        alt: 'Settings',
    },
    user: {
        path: '/assets/icons/user.svg',
        format: 'svg',
        alt: 'User',
    },
    home: {
        path: '/assets/icons/home.svg',
        format: 'svg',
        alt: 'Home',
    },
    'arrow-left': {
        path: '/assets/icons/arrow-left.svg',
        format: 'svg',
        alt: 'Arrow left',
        colorType: 'stroke',
    },
    'arrow-right': {
        path: '/assets/icons/arrow-right.svg',
        format: 'svg',
        alt: 'Arrow right',
        colorType: 'stroke',
    },
    'arrow-up': {
        path: '/assets/icons/arrow-up.svg',
        format: 'svg',
        alt: 'Arrow up',
        colorType: 'stroke',
    },
    'arrow-down': {
        path: '/assets/icons/arrow-down.svg',
        format: 'svg',
        alt: 'Arrow down',
        colorType: 'stroke',
    },
    sun: {
        path: '/assets/icons/sun.svg',
        format: 'svg',
        alt: 'Sun',
        colorType: 'fill',
    },
    moon: {
        path: '/assets/icons/moon.svg',
        format: 'svg',
        alt: 'Moon',
    },
    gear: {
        path: '/assets/icons/gear.svg',
        format: 'svg',
        alt: 'Gear',
    },
    gdrive: {
        path: '/assets/icons/gdrive.svg',
        format: 'svg',
        alt: 'Gsheets',
        colorType: 'original',
    },
    gsheets: {
        path: '/assets/icons/gsheets.svg',
        format: 'svg',
        alt: 'GSheets',
        colorType: 'original',
    },
    document: {
        path: '/assets/icons/document.svg',
        format: 'svg',
        alt: 'Document',
    },
    gmail: {
        path: '/assets/icons/gmail.svg',
        format: 'svg',
        alt: 'Gmail',
        colorType: 'original',
    },
    accounting: {
        path: '/assets/icons/accounting.svg',
        format: 'svg',
        alt: 'Accounting',
    },
    'arrow-exchange': {
        path: '/assets/icons/arrow-exchange.svg',
        format: 'svg',
        alt: 'Arrow exchange',
    },
    bank: {
        path: '/assets/icons/bank.svg',
        format: 'svg',
        alt: 'Bank',
    },
    mail: {
        path: '/assets/icons/mail.svg',
        format: 'svg',
        alt: 'Bank',
    },
    plus: {
        path: '/assets/icons/plus.svg',
        format: 'svg',
        alt: 'Plus',
    },
    bellz: {
        path: '/assets/icons/bellz.svg',
        format: 'svg',
        alt: 'Bell Z',
        colorType: 'fill',
    },
    play: {
        path: '/assets/icons/play.svg',
        format: 'svg',
        alt: 'Play',
        colorType: 'fill',
    },
    'chat-dots': {
        path: '/assets/icons/chat-dots.svg',
        format: 'svg',
        alt: 'Chat dots',
        colorType: 'fill',
    },
    'triangle-down': {
        path: '/assets/icons/triangle-down.svg',
        format: 'svg',
        alt: 'Chat dots',
        colorType: 'fill',
    },
    'check-circle': {
        path: '/assets/icons/check-circle.svg',
        format: 'svg',
        alt: 'Check circle',
        colorType: 'fill',
    },
    warning: {
        path: '/assets/icons/warning.svg',
        format: 'svg',
        alt: 'Warning',
        colorType: 'fill',
    },
    'caret-double-left': {
        path: '/assets/icons/caret-double-left.svg',
        format: 'svg',
        alt: 'Caret double left',
        colorType: 'fill',
    },
    'caret-double-right': {
        path: '/assets/icons/caret-double-right.svg',
        format: 'svg',
        alt: 'Caret double right',
        colorType: 'fill',
    },
    copy: {
        path: '/assets/icons/copy.svg',
        format: 'svg',
        alt: 'Copy',
        colorType: 'fill',
    },
    globe: {
        path: '/assets/icons/globe.svg',
        format: 'svg',
        alt: 'Globe',
        colorType: 'fill',
    },
    exchange: {
        path: '/assets/icons/exchange.svg',
        format: 'svg',
        alt: 'Exchange',
        colorType: 'fill',
    },
    mcp: {
        path: '/assets/provider-icons/mcp.svg',
        format: 'svg',
        alt: 'MCP',
        colorType: 'fill',
    },
    banknotes: {
        path: '/assets/icons/banknotes.svg',
        format: 'svg',
        alt: 'Banknotes',
        colorType: 'fill',
    },
    'building-office': {
        path: '/assets/icons/building-office.svg',
        format: 'svg',
        alt: 'Building office',
        colorType: 'fill',
    },
    car: {
        path: '/assets/icons/car.svg',
        format: 'svg',
        alt: 'Car',
        colorType: 'fill',
    },
    chart: {
        path: '/assets/icons/chart.svg',
        format: 'svg',
        alt: 'Chart',
        colorType: 'fill',
    },
    'currency-dollar': {
        path: '/assets/icons/currency-dollar.svg',
        format: 'svg',
        alt: 'Currency dollar',
        colorType: 'fill',
    },
    'document-text': {
        path: '/assets/icons/document-text.svg',
        format: 'svg',
        alt: 'Document text',
        colorType: 'fill',
    },
    gift: {
        path: '/assets/icons/gift.svg',
        format: 'svg',
        alt: 'Gift',
        colorType: 'fill',
    },
    'shopping-cart': {
        path: '/assets/icons/shopping-cart.svg',
        format: 'svg',
        alt: 'Shopping cart',
        colorType: 'fill',
    },
    'user-group': {
        path: '/assets/icons/user-group.svg',
        format: 'svg',
        alt: 'User group',
        colorType: 'fill',
    },
    wrench: {
        path: '/assets/icons/wrench.svg',
        format: 'svg',
        alt: 'Wrench',
        colorType: 'fill',
    },
    reload: {
        path: '/assets/icons/reload.svg',
        format: 'svg',
        alt: 'Reload',
        colorType: 'fill',
    },
    help: {
        path: '/assets/icons/help.svg',
        format: 'svg',
        alt: 'Help',
        colorType: 'fill',
    },
    magic: {
        path: '/assets/icons/magic.svg',
        format: 'svg',
        alt: 'Magic',
        colorType: 'fill',
    },
    'caret-down': {
        path: '/assets/icons/caret-down.svg',
        format: 'svg',
        alt: 'Caret down',
        colorType: 'fill',
    },
    edit: {
        path: '/assets/icons/edit.svg',
        format: 'svg',
        alt: 'Edit',
        colorType: 'fill',
    },
    pdfdoc: {
        path: '/assets/icons/pdfdoc.svg',
        format: 'svg',
        alt: 'PDF document',
        colorType: 'fill',
    },
    getmyinvoices: {
        path: '/assets/provider-icons/getmyinvoices.png',
        format: 'png',
        alt: 'GetMyInvoices',
    },
    'taxing-logo': {
        path: '/assets/logos/logo.svg',
        format: 'svg',
        alt: 'Taxing Logo',
        colorType: 'original',
    },
    puzzle: {
        path: '/assets/icons/puzzle.svg',
        format: 'svg',
        alt: 'Puzzle',
        colorType: 'fill',
    },
}

/**
 * Get icon definition by name
    'tailride-logo': {
        path: '/assets/provider-icons/tailride-logo.png',
        format: 'png',
        alt: 'TailRide Logo',
    },
    'invoiceradar-logo': {
        path: '/assets/provider-icons/invoiceradar-logo.png',
        format: 'png',
        alt: 'InvoiceRadar Logo',
    },
 * @param name - Icon name
 * @returns IconDefinition or undefined if not found
 */
export const getIconDefinition = (name: string): IconDefinition | undefined => {
    return ICON_MAP[name]
}

/**
 * Check if an icon name exists in the dictionary
 * @param name - Icon name
 * @returns boolean
 */
export const hasIcon = (name: string): boolean => {
    return name in ICON_MAP
}

/**
 * Get all available icon names
 * @returns Array of icon names
 */
export const getAllIconNames = (): string[] => {
    return Object.keys(ICON_MAP)
}
